/**
 * Sticky floor level — the single source of truth for "how high is the
 * ground?", shared by the placement gauge and the wheel placement.
 *
 * Why this exists (2026-08-22): the gauge and the wheel both answered
 * that question from `driver.getGroundHit()`, but with opposite
 * fallbacks. On a dropout the gauge held its last line while the wheel
 * fell back to the depth sample's own Y — aim height on whatever
 * vertical surface the ray had struck. The green line stayed on the
 * floor and the wheel climbed the back plane, which is what the field
 * test reported.
 *
 * The dropout is systematic, not incidental: the downward-angled ground
 * ray is occluded by the car body at exactly the aim the product asks
 * for (the wheel/floor junction). So "hold the last good reading" isn't
 * a nicety — it's the normal operating mode.
 *
 * Holding indefinitely is correct here: a floor does not move during a
 * placement session. Pose drift over a session is centimetres, well
 * inside the 2-3cm placement tolerance this work targets, and a stale
 * floor is strictly better than the alternative that shipped — a wheel
 * pinned to a wall a metre up.
 *
 * Pure and frame-rate independent; the caller owns the estimate.
 */

/** A floor sample must be at least this far below the camera. Anything
 *  closer is the ray grazing a car flank, a table edge, or the phone's
 *  own near field — not the ground. */
export const FLOOR_MIN_DROP_M = 0.4

/** …and no further. Beyond this the reading is a stairwell, a hole in
 *  the depth map, or a bad triangulation. */
export const FLOOR_MAX_DROP_M = 3

/** A surface counts as ground-like when its normal points mostly up.
 *  0.7 is ~45 degrees of tilt, which comfortably admits a real floor
 *  read at an angle while excluding anything wall-like. */
export const FLOOR_NORMAL_MIN_Y = 0.7

export type FloorEstimate = { readonly y: number } | null

/**
 * Is this hit lying on a roughly horizontal surface?
 *
 * The height gate below cannot answer this on its own, and that is the
 * point of having both. A hit on a car door sits perhaps 0.8m under a
 * held phone — a perfectly plausible floor height — so height alone
 * would let the middle of the car become "the ground". Orientation is
 * the discriminator: a vertical plane is never a floor, wherever it
 * sits.
 *
 * Hits with no normal are treated as NOT ground-like: this predicate
 * guards a source that redefines the floor, so silence should not
 * grant permission.
 */
export function isHorizontalSurface(
  hit: { normal?: { y?: number | null } | null } | null | undefined,
): boolean {
  const ny = hit?.normal?.y
  return typeof ny === 'number' && Number.isFinite(ny) && Math.abs(ny) >= FLOOR_NORMAL_MIN_Y
}

/**
 * Is this a believable ground reading, given where the camera is?
 *
 * The gate is deliberately coarse. It cannot tell a floor from a low
 * kerb — both are plausibly ~1.2m under a held phone — and it does not
 * try to. Its job is rejecting the GROSS error that caused the bug: a
 * reading at aim height on a vertical surface, which is what arrives
 * whenever the ray hits the car instead of the ground.
 */
export function isPlausibleFloorSample(sampleY: number, cameraY: number): boolean {
  const drop = cameraY - sampleY
  return drop >= FLOOR_MIN_DROP_M && drop <= FLOOR_MAX_DROP_M
}

/**
 * Folds one ground reading into the estimate.
 *
 * Returns the PREVIOUS estimate object unchanged when the sample is
 * missing or implausible, so callers can cheaply detect "nothing
 * learned" by identity.
 *
 * A null return means the floor is still unknown — no plausible reading
 * has arrived yet this session. Consumers must treat that as "cannot
 * place", never as "place at zero": guessing a floor is how the wheel
 * ended up on a wall.
 */
export function updateFloorEstimate(
  prev: FloorEstimate,
  sampleY: number | null | undefined,
  cameraY: number,
): FloorEstimate {
  if (sampleY == null || !Number.isFinite(sampleY)) return prev
  if (!isPlausibleFloorSample(sampleY, cameraY)) return prev
  return { y: sampleY }
}
